import React from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight, Instagram } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = [
    {
      title: 'Services',
      links: [
        { label: 'Business Website', href: '#services' },
        { label: 'Personal Website', href: '#services' },
        { label: 'Landing Page', href: '#services' },
        { label: 'Custom Website', href: '#services' },
      ],
    },
    {
      title: 'Company',
      links: [
        { label: 'About Us', href: '#' },
        { label: 'Our Process', href: '#process' },
        { label: 'Portfolio', href: '#portfolio' },
        { label: 'Contact', href: '#contact' },
      ],
    },
    {
      title: 'Legal',
      links: [
        { label: 'Privacy Policy', href: '#' },
        { label: 'Terms of Service', href: '#' },
        { label: 'Cookie Policy', href: '#' },
      ],
    },
  ];

  const scrollToSection = (href) => {
    if (href.startsWith('#')) {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <footer 
      className="relative overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, #050508 0%, #08080f 100%)',
      }}
      data-testid="footer"
    >
      {/* Background glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[300px] bg-[#7B2EFF]/5 rounded-full blur-[150px]" />
      </div>

      {/* Top border glow */}
      <div className="h-px bg-gradient-to-r from-transparent via-[#7B2EFF]/30 to-transparent" />

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 relative z-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {/* Brand Column */}
          <div className="col-span-2 md:col-span-1">
            <a href="#" className="inline-block" data-testid="footer-logo">
              <span className="text-2xl font-bold text-white">
                WEB<span className="text-transparent bg-clip-text bg-gradient-to-r from-[#7B2EFF] to-[#00D4FF]">.</span>K
              </span>
            </a>
            <p className="mt-4 text-sm text-white/40 max-w-xs leading-relaxed">
              Premium web development studio crafting futuristic, minimal, and high-performance digital experiences.
            </p>
            
            {/* Social Icons */}
            <div className="mt-6 flex items-center gap-3">
              <motion.a
                href="https://instagram.com/personalweb_builder"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-xl glass-card flex items-center justify-center group"
                whileHover={{ scale: 1.1, boxShadow: '0 0 20px rgba(123, 46, 255, 0.3)' }}
                data-testid="footer-social-instagram"
              >
                <Instagram className="w-4 h-4 text-white/60 group-hover:text-[#E1306C] transition-colors" />
              </motion.a>
            </div>
          </div>

          {/* Link Columns */}
          {footerLinks.map((column) => (
            <div key={column.title}>
              <h4 className="text-sm font-semibold text-white mb-4">{column.title}</h4>
              <ul className="space-y-3">
                {column.links.map((link) => (
                  <li key={link.label}>
                    <button
                      onClick={() => scrollToSection(link.href)}
                      className="text-sm text-white/40 hover:text-white transition-colors inline-flex items-center gap-1 group"
                      data-testid={`footer-link-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                    >
                      {link.label}
                      <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-white/10 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-sm text-white/30">
            © {currentYear} WEB.K. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <a
              href="https://instagram.com/personalweb_builder"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-white/30 hover:text-[#7B2EFF] transition-colors"
            >
              <Instagram className="w-4 h-4" />
              @personalweb_builder
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
